import { FunctionComponent, useEffect, useState } from "react";
import {
    Badge,
    Box,
    Card,
    CardBody,
    Center,
    Flex,
    Grid,
    Heading,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Spinner,
    Text,
    Tooltip,
    useBoolean,
    VStack,
} from "@chakra-ui/react";
import { ClientResponse, useClients, useMutateClientFavorite } from "../../api";
import { IconPin, IconPinFilled, IconPinnedOff, IconSearch, IconX } from "@tabler/icons-react";
import fuzzysort from "fuzzysort";
import { useNavigate } from "react-router-dom";
import { SetPageTitle } from "../../components/SetPageTitle";

// Deterministic avatar color from client name
const avatarColors = ["blue", "teal", "green", "purple", "orange", "pink", "cyan", "red"];
function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
}

export const ClientSelectionPage: FunctionComponent<{ fixed?: boolean }> = () => {
    const navigate = useNavigate();
    const [filterText, setFilterText] = useState<string>("");

    const clientsQuery = useClients(),
        clientsData = (clientsQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));

    useEffect((): any => {
        if (clientsQuery && !clientsQuery.isLoading && clientsQuery.data && clientsQuery.data.length === 1) {
            navigate(`${clientsQuery.data[0].id}`);
        }
    }, [clientsQuery.data]);

    if (clientsQuery.isLoading || !clientsQuery.data) return <Spinner />;
    if (clientsQuery.data.length === 0) return <Text>No clients available.</Text>;

    const pinnedClients = clientsData.filter(c => c.isFavorite);
    const hasPinned = pinnedClients.length > 0;

    const filteredClients =
        filterText.length === 0 ? clientsData : fuzzysort.go<ClientResponse>(filterText, clientsData, { keys: ["name", "description"] }).map(t => t.obj);

    const filteredPinned = filteredClients.filter(c => c.isFavorite);
    const filteredAll = filteredClients;

    return (
        <>
            <SetPageTitle title={"Select Client"} />
            <VStack align={"stretch"} spacing={"2rem"}>
                {/* Search */}
                <InputGroup size={"md"} maxW={"24rem"}>
                    <InputLeftElement pointerEvents={"none"}>
                        <Icon as={IconSearch} color={"gray.400"} />
                    </InputLeftElement>
                    <Input
                        placeholder={"Search clients…"}
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        pr={filterText ? "2.5rem" : undefined}
                    />
                    {filterText && (
                        <InputRightElement>
                            <IconButton
                                aria-label={"Clear search"}
                                icon={<Icon as={IconX} />}
                                size={"xs"}
                                variant={"ghost"}
                                onClick={() => setFilterText("")}
                            />
                        </InputRightElement>
                    )}
                </InputGroup>

                {/* Pinned section */}
                {hasPinned && filteredPinned.length > 0 && (
                    <VStack align={"stretch"} spacing={"0.75rem"}>
                        <Heading size={"sm"} color={"gray.500"} textTransform={"uppercase"} letterSpacing={"wider"}>
                            <Icon as={IconPinFilled} boxSize={"0.85em"} verticalAlign={"middle"} mr={1} color={"blue.400"} />
                            Pinned
                        </Heading>
                        <ClientGrid clients={filteredPinned} isPinnedSection />
                    </VStack>
                )}

                {/* All clients */}
                <VStack align={"stretch"} spacing={"0.75rem"}>
                    <Heading size={"sm"} color={"gray.500"} textTransform={"uppercase"} letterSpacing={"wider"}>
                        {hasPinned ? "All Clients" : "Clients"}
                    </Heading>
                    {filteredAll.length === 0 ? <Text color={"gray.400"}>No clients match your search.</Text> : <ClientGrid clients={filteredAll} />}
                </VStack>
            </VStack>
        </>
    );
};

const ClientGrid: FunctionComponent<{ clients: ClientResponse[]; isPinnedSection?: boolean }> = ({ clients, isPinnedSection }) => (
    <Grid templateColumns={"repeat(auto-fill, minmax(260px, 1fr))"} gap={"1rem"}>
        {clients.map(client => (
            <ClientCard key={client.id} client={client} isPinnedSection={isPinnedSection} />
        ))}
    </Grid>
);

const ClientCard: FunctionComponent<{ client: ClientResponse; isPinnedSection?: boolean }> = ({ client, isPinnedSection }) => {
    const navigate = useNavigate();
    const [isHovering, hover] = useBoolean(false);
    const color = getAvatarColor(client.name);

    return (
        <Card
            variant={"withShadow"}
            cursor={"pointer"}
            onClick={() => navigate(`${client.id}`)}
            onMouseEnter={hover.on}
            onMouseLeave={hover.off}
            borderWidth={"1px"}
            borderColor={isHovering ? `${color}.300` : isPinnedSection ? "blue.200" : "transparent"}
            bg={isPinnedSection ? "blue.50" : undefined}
            transform={isHovering ? "translateY(-2px)" : undefined}
            transition={"all 0.15s ease"}
            boxShadow={isHovering ? "md" : undefined}
        >
            <CardBody p={"1.25rem"}>
                <Flex justify={"space-between"} align={"start"} mb={"0.75rem"}>
                    {/* Avatar */}
                    <Center
                        w={"2.5rem"}
                        h={"2.5rem"}
                        borderRadius={"full"}
                        bg={`${color}.100`}
                        color={`${color}.700`}
                        fontWeight={"bold"}
                        fontSize={"lg"}
                        flexShrink={0}
                    >
                        {client.name.charAt(0).toUpperCase()}
                    </Center>

                    {/* Pin button */}
                    <Box onClick={e => e.stopPropagation()}>
                        <PinButton client={client} />
                    </Box>
                </Flex>

                <Text fontWeight={"semibold"} fontSize={"md"} noOfLines={1} mb={"0.25rem"}>
                    {client.name}
                </Text>
                {client.description ? (
                    <Text fontSize={"sm"} color={"gray.500"} noOfLines={2}>
                        {client.description}
                    </Text>
                ) : (
                    <Text fontSize={"sm"} color={"gray.300"} fontStyle={"italic"}>
                        No description
                    </Text>
                )}
                {client.isInternal && (
                    <Badge mt={"0.5rem"} colorScheme={"purple"} fontSize={"xs"}>
                        Internal
                    </Badge>
                )}
            </CardBody>
        </Card>
    );
};

const PinButton: FunctionComponent<{ client: ClientResponse }> = ({ client }) => {
    const [isMouseIn, setMouseIn] = useBoolean(false);
    const clientFavorite = useMutateClientFavorite();

    const icon = client.isFavorite ? (isMouseIn ? IconPinnedOff : IconPinFilled) : isMouseIn ? IconPinFilled : IconPin;

    const buttonAction = client.isFavorite ? "Unpin client" : "Pin client";

    return (
        <Tooltip label={buttonAction}>
            <IconButton
                aria-label={buttonAction}
                icon={<Icon as={icon} color={client.isFavorite || isMouseIn ? "blue.400" : "gray.300"} />}
                variant={"ghost"}
                size={"sm"}
                onMouseEnter={setMouseIn.on}
                onMouseLeave={setMouseIn.off}
                onClick={() => clientFavorite.mutate({ clientId: client.id, action: client.isFavorite ? "remove" : "add" })}
            />
        </Tooltip>
    );
};
